/**
 * Editor state of one breadcrumb segment override row. `id` identifies the row in the DOM.
 * @typedef {Object} BreadcrumbOverride
 * @property {number} id
 * @property {string} segment
 * @property {string} label
 * @property {string} href
 */

/**
 * @param {import('../../shell/types/client.types.js').TemplateBreadcrumbConfig | null | undefined} breadcrumbs
 * @param {() => number} nextId supplies a fresh row id per override
 * @returns {BreadcrumbOverride[]}
 */
export function toBreadcrumbOverrides(breadcrumbs, nextId) {
  return Object.entries(breadcrumbs?.segments || {})
    .map(([segment, config]) => ({
      id: nextId(),
      segment,
      label: config?.label || '',
      href: config?.href || '',
    }));
}

/**
 * @param {BreadcrumbOverride[]} overrides
 * @returns {{id: number, segment: string} | null} the first override whose trimmed segment repeats an earlier one
 */
export function findDuplicateBreadcrumbOverride(overrides) {
  const seenSegments = new Set();
  for (const override of overrides) {
    const segment = String(override.segment || '').trim();
    if (seenSegments.has(segment)) {
      return {id: override.id, segment};
    }
    seenSegments.add(segment);
  }
  return null;
}

/**
 * Builds the API shape of the overrides. Rows without a segment are skipped, and no config is
 * sent when nothing remains.
 *
 * @param {BreadcrumbOverride[]} overrides
 * @returns {import('../../shell/types/client.types.js').TemplateBreadcrumbConfig | null}
 */
export function toBreadcrumbConfig(overrides) {
  const segments = {};
  for (const override of overrides) {
    const segment = String(override.segment || '').trim();
    if (!segment) {
      continue;
    }
    segments[segment] = {
      label: String(override.label || '').trim(),
      href: String(override.href || '').trim(),
    };
  }
  return Object.keys(segments).length > 0 ? {segments} : null;
}
